import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { X, Share2, Download } from 'lucide-react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Zoom, Preload } from 'swiper/modules';
import StarRating from '../shared/StarRating';
import ShareModal from '../modals/ShareModal';

// Swiper styles
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/zoom';

// Preload image helper
const preloadImage = (url) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(url);
    img.onerror = reject;
    img.src = url;
  });
};

export default function Lightbox({ images, initialIndex, onClose, onRatingChange, readOnly = false }) {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [showShareModal, setShowShareModal] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [swiperInstance, setSwiperInstance] = useState(null);
  const [loadedImages, setLoadedImages] = useState(new Set());
  const [loadingStates, setLoadingStates] = useState({});
  
  // Kein Proxy — alle Medien liegen lokal
  const getProxyUrl = useCallback((externalUrl) => externalUrl || '', []);

  // Preload current + adjacent images
  useEffect(() => {
    const indicesToPreload = [
      currentIndex,
      currentIndex + 1,
      currentIndex - 1,
      currentIndex + 2,
    ].filter(i => i >= 0 && i < images.length);

    indicesToPreload.forEach(idx => {
      const url = getProxyUrl(images[idx]?.url);
      if (url && !loadedImages.has(idx)) {
        preloadImage(url).then(() => {
          setLoadedImages(prev => new Set([...prev, idx]));
        }).catch(() => {
          // Image failed to load, still mark as attempted
          setLoadedImages(prev => new Set([...prev, idx]));
        });
      }
    });
  }, [currentIndex, images, getProxyUrl, loadedImages]);

  const handleImageLoad = (idx) => {
    setLoadingStates(prev => ({ ...prev, [idx]: false }));
    setLoadedImages(prev => new Set([...prev, idx]));
  };

  const handleImageLoadStart = (idx) => {
    if (!loadedImages.has(idx)) {
      setLoadingStates(prev => ({ ...prev, [idx]: true }));
    }
  };

  const handleDownload = async () => {
    const currentImage = images[currentIndex];
    setIsDownloading(true);
    try {
      const response = await fetch(getProxyUrl(currentImage.url));
      if (!response.ok) throw new Error("Download failed");
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `snapshot-${currentIndex + 1}.jpg`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Download error:", error);
    } finally {
      setIsDownloading(false);
    }
  };

  // Lock body scroll
  useEffect(() => {
    const scrollY = window.scrollY;
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = '100%';

    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      window.scrollTo(0, scrollY);
    };
  }, []);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft') swiperInstance?.slidePrev();
      if (e.key === 'ArrowRight') swiperInstance?.slideNext();
      if (e.key === 'Escape') onClose(currentIndex);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentIndex, swiperInstance, onClose]);

  const currentImage = images[currentIndex];

  return createPortal(
    <div className="fixed inset-0 z-[99999] bg-black flex flex-col">
      {/* HEADER */}
      <div className="flex items-center justify-between p-4 z-[100] relative bg-gradient-to-b from-black/80 to-transparent">
        <div className="text-white/80 text-sm font-medium">
          {currentIndex + 1} / {images.length}
        </div>

        <div className="flex items-center gap-2">
          {/* Download - Desktop */}
          <button
            onClick={handleDownload}
            disabled={isDownloading}
            className="hidden md:flex w-10 h-10 items-center justify-center rounded-full border-2 border-white/30 text-white hover:bg-blue-500 hover:border-blue-500 transition-all disabled:opacity-50"
          >
            {isDownloading ? (
              <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
            ) : (
              <Download className="w-5 h-5" />
            )}
          </button>

          {/* Share - Desktop */}
          {!readOnly && (
            <button
              onClick={() => setShowShareModal(true)}
              className="hidden md:flex w-10 h-10 items-center justify-center rounded-full border-2 border-white/30 text-white hover:bg-[#10B981] hover:border-[#10B981] transition-all"
            >
              <Share2 className="w-5 h-5" />
            </button>
          )}

          {/* Close */}
          <button
            onClick={() => onClose(currentIndex)}
            className="w-10 h-10 flex items-center justify-center rounded-full border-2 border-white/30 text-white hover:bg-white/20 transition-all"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* SWIPER */}
      <div className="flex-1 relative">
        <Swiper
          modules={[Navigation, Pagination, Zoom]}
          initialSlide={initialIndex}
          spaceBetween={0}
          slidesPerView={1}
          navigation={{ enabled: true }}
          pagination={{ clickable: true, dynamicBullets: true }}
          zoom={{ maxRatio: 4, minRatio: 1 }}
          onSwiper={setSwiperInstance}
          onSlideChange={(swiper) => setCurrentIndex(swiper.activeIndex)}
          className="h-full w-full lightbox-swiper"
          style={{ 
            '--swiper-navigation-color': '#fff',
            '--swiper-pagination-color': '#fff',
          }}
        >
          {images.map((image, idx) => (
            <SwiperSlide key={image.id || idx} className="flex items-center justify-center">
              <div className="swiper-zoom-container flex items-center justify-center h-full relative">
                {/* Loading Spinner */}
                {loadingStates[idx] && (
                  <div className="absolute inset-0 flex items-center justify-center z-10">
                    <div className="w-10 h-10 border-4 border-white/20 border-t-white rounded-full animate-spin"></div>
                  </div>
                )}
                <img
                  src={getProxyUrl(image.url)}
                  alt=""
                  className={`max-w-full max-h-[75vh] object-contain select-none transition-opacity duration-300 ${
                    loadingStates[idx] ? 'opacity-0' : 'opacity-100'
                  }`}
                  draggable={false}
                  onLoadStart={() => handleImageLoadStart(idx)}
                  onLoad={() => handleImageLoad(idx)}
                  onError={() => handleImageLoad(idx)}
                />
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
      </div>

      {/* MOBILE CONTROLS */}
      <div className="sm:hidden absolute bottom-6 left-0 right-0 flex justify-center gap-3 z-[100]">
        <div className="bg-black/90 backdrop-blur-sm px-4 py-3 rounded-full border border-white/20">
          <StarRating
            imageId={currentImage?.id}
            initialRating={currentImage?.rating || 0}
            onChange={onRatingChange}
          />
        </div>

        {!readOnly && (
          <button
            onClick={() => setShowShareModal(true)}
            className="bg-black/90 backdrop-blur-sm px-5 py-3 rounded-full border border-white/20 text-white flex items-center gap-2"
          >
            <Share2 className="w-4 h-4" />
            <span className="text-sm font-medium">Share</span>
          </button>
        )}
      </div>

      {/* DESKTOP FOOTER */}
      <div className="hidden sm:block p-4 bg-gradient-to-t from-black/80 to-transparent">
        <div className="flex items-center justify-center gap-3">
          <span className="text-white/60 text-sm">Bewertung:</span>
          <StarRating
            imageId={currentImage?.id}
            initialRating={currentImage?.rating || 0}
            onChange={onRatingChange}
          />
        </div>
      </div>

      {showShareModal && (
        <ShareModal
          imageUrl={currentImage?.url}
          onClose={() => setShowShareModal(false)}
        />
      )}

      {/* Custom Swiper Styles */}
      <style>{`
        .lightbox-swiper {
          --swiper-navigation-size: 28px;
        }
        .lightbox-swiper .swiper-button-prev,
        .lightbox-swiper .swiper-button-next {
          background: rgba(0,0,0,0.6);
          width: 44px;
          height: 44px;
          border-radius: 50%;
          border: 2px solid rgba(255,255,255,0.3);
        }
        .lightbox-swiper .swiper-button-prev:hover,
        .lightbox-swiper .swiper-button-next:hover {
          background: rgba(255,255,255,0.2);
        }
        .lightbox-swiper .swiper-pagination {
          bottom: 80px !important;
        }
        @media (min-width: 640px) {
          .lightbox-swiper .swiper-pagination {
            bottom: 20px !important;
          }
        }
        .lightbox-swiper .swiper-pagination-bullet {
          background: white;
          opacity: 0.5;
        }
        .lightbox-swiper .swiper-pagination-bullet-active {
          opacity: 1;
        }
        .swiper-zoom-container {
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
        }
      `}</style>
    </div>,
    document.body
  );
}
